import { useEffect, useRef, useState } from 'react';
import { playPrompt, type PromptPlayback } from '../promptPlayer';
import { diag } from '../diag';

interface UsePromptPlayerOptions {
  /** The clip actually started producing sound. */
  onStarted?: () => void;
  /** The clip played to the end. */
  onEnded?: () => void;
  /**
   * The clip stopped without ending and without this player's {@link
   * UsePromptPlayerResult.stop}: an iOS interruption, or another prompt started.
   */
  onStoppedElsewhere?: () => void;
  /** The clip couldn't be played (e.g. it failed to load or decode). */
  onError?: (error: unknown) => void;
}

interface UsePromptPlayerResult {
  isPlaying: boolean;
  /** Play a clip, stopping this player's current one. Call from a tap handler. */
  play: (url: string, label?: string) => void;
  /** Stop this player's clip, if any. */
  stop: () => void;
}

/**
 * React adapter for the shared prompt player: tracks whether this component's
 * clip is playing and stops it on unmount. Callbacks are read fresh on every
 * event.
 */
export function usePromptPlayer(
  options: UsePromptPlayerOptions = {}
): UsePromptPlayerResult {
  const [isPlaying, setIsPlaying] = useState(false);
  const playbackRef = useRef<PromptPlayback | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Stop whatever is playing when the player leaves the screen.
  useEffect(
    () => (): void => {
      playbackRef.current?.stop();
      playbackRef.current = null;
    },
    []
  );

  const stoppedElsewhere = (): void => {
    playbackRef.current = null;
    setIsPlaying(false);
    optionsRef.current.onStoppedElsewhere?.();
  };

  const stop = (): void => {
    playbackRef.current?.stop();
    playbackRef.current = null;
    setIsPlaying(false);
  };

  const play = (url: string, label = 'natural'): void => {
    playbackRef.current?.stop();
    diag('AudioPlayer: play', label);
    const playback = playPrompt(url, {
      onEnded: () => {
        playbackRef.current = null;
        setIsPlaying(false);
        optionsRef.current.onEnded?.();
      },
      onInterrupted: stoppedElsewhere,
      onSuperseded: stoppedElsewhere,
    });
    playbackRef.current = playback;
    setIsPlaying(true);
    playback.started
      .then(() => {
        if (playbackRef.current === playback) optionsRef.current.onStarted?.();
      })
      .catch((error: unknown) => {
        if (playbackRef.current !== playback) return;
        playbackRef.current = null;
        setIsPlaying(false);
        optionsRef.current.onError?.(error);
      });
  };

  return { isPlaying, play, stop };
}
